import { call, all, select, put, type CallEffect } from 'redux-saga/effects';
import { envelopeApi } from '../../../lib/api/envelope';
import {
  addBlock,
  createNewGroup,
  deleteBlock,
  deleteBlockGroup,
  editBlockGroup,
  editBlock,
} from '../../../lib/api/envelope/envelope-api';
import { BLOCK_TYPE_KEYS } from '../../../lib/constants';
import type { IResponse } from '../../../lib/types';
import { calculateFontSize } from '../../../lib/useAutomaticFontSizing';
import { cleanUpInvalidBlockValuesAndLogToFaro, getRecipientColor } from '../../../lib/utils';
import type { IRootState } from '../../index';
import { setAutoSaving } from '../actions';
import type { IGroupState } from '../reducers';
import {
  type ProcessedEnvelope,
  type IBlock,
  type IBlocks,
  type IEnvelope,
  type IGroup,
  type IPageDimensions,
  type ISigningGroup,
} from '../types';

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const getId = (state: IRootState) => state.senderBuild.id;
// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const getGroups = (state: IRootState) => state.senderBuild.groups;
// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const getBlocks = (state: IRootState) => state.senderBuild.blocks;
// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const getZoom = (state: IRootState) => state.senderBuild.zoom;

export function* envelopeFetch(id: string) {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const zoom = yield getZoom;
  const res: IResponse<IEnvelope> = yield call(envelopeApi.getEnvelopeById, id);

  if (!res.success) throw new Error('Failed to fetch envelope');
  return processEnvelope({ envelope: res.result, zoom });
}

export function processEnvelope(args: { envelope: IEnvelope; zoom: number }): ProcessedEnvelope {
  const { envelope, zoom } = args;

  const carbonCopy = envelope.carbonCopy.map((recipient, _i: number) => ({
    ...recipient,
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    color: getRecipientColor(recipient.recipientColorCounter),
  }));
  let signingGroups: ISigningGroup[] = [];
  if (envelope.signingGroups) {
    signingGroups = envelope.signingGroups
      .map((sg) => ({ name: sg.name, id: sg.id, order: sg.order }))
      .sort((a, b) => a.order - b.order);
  }
  const pageDimensions: IPageDimensions = {};
  envelope.documents.map((document) => {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    pageDimensions[document.documentId] = document.pages;
    document.isDownloaded = false;
  });

  const blocks: IBlocks = {};
  const groups: IGroupState = {};
  envelope.documents.forEach((document) => {
    blocks[document.documentId] = {};
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    pageDimensions[document.documentId] = document.pages;
    const currentGroups = document.blockGroups;
    if (currentGroups) {
      currentGroups.forEach((group) => {
        if (!groups[group.documentId]) {
          groups[group.documentId] = {};
        }
        if (!groups[group.documentId][group.pageNumber]) {
          groups[group.documentId][group.pageNumber] = {};
        }
        groups[group.documentId][group.pageNumber][group.id] = group;
      });
    }
    for (let i = 0; i < document.numberOfPages!; i++) {
      blocks[document.documentId][i] = [];
    }
    document.blocks?.forEach((block) => {
      if (block.blockType === BLOCK_TYPE_KEYS.TEXT && block.value && !block.fontSize) {
        block.fontSize = calculateFontSize(block.value as string, 22, block.height!, block.width!, zoom);
      }
      blocks[document.documentId][block.pageNumber].push(block);
    });
  });

  return {
    ...envelope,
    blocks,
    groups,
    carbonCopy,
    signingGroups,
    pageDimensions,
  };
}

export function* senderCreateBlock(documentId: string, _pageIndex: number, block: IBlock) {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const envelopeId = yield select(getId);
  const { result } = yield call(addBlock, envelopeId, documentId, block);
  return result.id;
}

export function* senderCreateBlocksThenGroup(newBlocks: IBlock[], newGroup: IGroup) {
  const apiCalls: Generator[] = [];
  newBlocks.forEach((newBlock) => {
    delete newBlock.blockId;
    delete newBlock.groupId;
    apiCalls.push(senderCreateBlock(newGroup.documentId, newGroup.pageNumber, newBlock));
  });
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const blockIds = yield all(apiCalls);
  newBlocks.forEach((newBlock, i) => {
    newBlock.blockId = blockIds[i];
  });
  newGroup.blockIds.push(...blockIds);
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const groupId = yield senderCreateGroup(newGroup);
  newGroup.id = groupId;
  for (let i = 0; i < newBlocks.length; i++) {
    newBlocks[i].groupId = groupId;
  }
}

export function* senderCreateGroup(group: IGroup) {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const envelopeId = yield select(getId);
  const { result } = yield call(createNewGroup, envelopeId, group);
  return result;
}

export function* senderEditBlock(documentId: string, pageIndex: number, blockIndex: number) {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const originalBlock = yield select((state: IRootState) => {
    return state.senderBuild.blocks[documentId][pageIndex][blockIndex];
  });
  yield put(setAutoSaving(true));
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const envelopeId = yield select(getId);
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const cleanBlock = yield cleanUpInvalidBlockValuesAndLogToFaro(
    originalBlock,
    envelopeId,
    'senderEditBlock: Block had invalid values'
  );
  try {
    yield call(editBlock, envelopeId, documentId, cleanBlock);
  } catch (e) {
    if (!e.message.includes('404')) {
      throw new Error(e);
    }
  }
}

export function* senderGroupEdit(documentId: string, pageIndex: number, groupId: string, updateObj: any) {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const originalGroup = yield select((state: IRootState) => {
    return state.senderBuild.groups[documentId][pageIndex][groupId];
  });
  const newGroup: IGroup = { ...originalGroup, ...updateObj };
  if (newGroup.blockIds.length <= newGroup.validation.value) {
    newGroup.validation.value = newGroup.blockIds.length;
  }
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const envelopeId = yield select(getId);
  yield call(editBlockGroup, envelopeId, newGroup);
}

export function* senderDeleteBlock(documentId: string, pageIndex: number, blockIds: string[]) {
  yield put(setAutoSaving(true));
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  const envelopeId = yield select(getId);
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  let pageGroups = yield select(getGroups);
  if (pageGroups[documentId]) {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    pageGroups = yield select((state: IRootState) => state.senderBuild.groups[documentId][pageIndex]);
  }
  let remainingBlockIds = [...blockIds];
  const apiCalls: CallEffect[] = [];
  for (const groupId in pageGroups) {
    const groupBlockIds = pageGroups[groupId].blockIds;
    if (groupBlockIds && groupBlockIds.every((blockId: string) => remainingBlockIds.includes(blockId))) {
      apiCalls.push(call(deleteBlockGroup, envelopeId, documentId, groupId));
      remainingBlockIds = remainingBlockIds.filter((blockId) => !groupBlockIds.includes(blockId));
    }
  }
  if (remainingBlockIds.length) {
    const blocks: IBlocks[] = yield select(getBlocks);
    remainingBlockIds.forEach((blockId) => {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      const block = blocks[documentId][pageIndex].find((b: IBlock) => b.blockId === blockId);
      // PATCH blockGroups endpoint will delete the block for us if it is removed from the blockIds array
      // If blockGroupId still exist in a sigle block - DIGI-2016
      if (!block?.groupId || !pageGroups?.[block.groupId]) {
        apiCalls.push(call(deleteBlock, envelopeId, documentId, blockId));
      }
    });
  }
  try {
    const res: number[] = yield all(apiCalls);
    if (res.every((status) => status !== 204)) {
      throw new Error('404');
    }
  } catch (e) {
    throw new Error(e);
  }
}
